import { Fragment, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { Guard } from "@/components/Guard";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { ArrowLeft, ExternalLink, FileText, Download } from "lucide-react";
import { fetchHelloAssoOrder } from "@/lib/helloasso-sync";
import { useAuth } from "@/lib/auth-context";
import { OrderImportDialog } from "@/components/OrderImportDialog";

export const Route = createFileRoute("/orders/$id")({
  component: () => (
    <Guard module="treasury">
      <OrderDetailPage />
    </Guard>
  ),
});

const fmtEUR = (n: number | string | null | undefined) =>
  new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format(Number(n ?? 0));

const fmtDateTime = (s: string | null) => {
  if (!s) return "—";
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleString("fr-FR");
};
const fmtDate = (s: string | null) => {
  if (!s) return "—";
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleDateString("fr-FR");
};

const ITEM_STATE_MAP: Record<string, { label: string; className: string }> = {
  Processed: { label: "Payé", className: "bg-success text-success-foreground hover:bg-success/90" },
  Registered: { label: "Enregistré", className: "bg-success/80 text-success-foreground hover:bg-success/70" },
  Waiting: { label: "En attente", className: "bg-warning text-warning-foreground hover:bg-warning/90" },
  Refunded: { label: "Remboursé", className: "bg-background text-foreground border border-border" },
  Refunding: { label: "Remboursement en cours", className: "bg-destructive text-destructive-foreground hover:bg-destructive/90" },
  Refused: { label: "Refusé", className: "bg-destructive text-destructive-foreground hover:bg-destructive/90" },
  Canceled: { label: "Annulé", className: "bg-muted text-muted-foreground" },
  Abandoned: { label: "Abandonné", className: "bg-muted text-muted-foreground" },
  Contested: { label: "Contesté", className: "bg-destructive/80 text-destructive-foreground" },
  Deleted: { label: "Supprimé", className: "bg-muted text-muted-foreground" },
  Unknown: { label: "Inconnu", className: "bg-muted text-muted-foreground" },
  Unknow: { label: "Inconnu", className: "bg-muted text-muted-foreground" },
};

export function itemStateBadge(state: string | null | undefined) {
  if (!state) return <Badge variant="outline">—</Badge>;
  const cfg = ITEM_STATE_MAP[state];
  if (!cfg) return <Badge variant="outline">{state}</Badge>;
  return <Badge className={cfg.className}>{cfg.label}</Badge>;
}

const PAYMENT_STATE_MAP: Record<string, { label: string; className: string }> = {
  Authorized: { label: "Autorisé", className: "bg-success text-success-foreground hover:bg-success/90" },
  Pending: { label: "En attente", className: "bg-warning text-warning-foreground hover:bg-warning/90" },
  Registered: { label: "Enregistré", className: "bg-success/80 text-success-foreground hover:bg-success/70" },
  Refused: { label: "Refusé", className: "bg-destructive text-destructive-foreground hover:bg-destructive/90" },
  Refunded: { label: "Remboursé", className: "bg-background text-foreground border border-border" },
  Refunding: { label: "Remboursement en cours", className: "bg-destructive text-destructive-foreground hover:bg-destructive/90" },
  Contested: { label: "Contesté", className: "bg-destructive/80 text-destructive-foreground" },
  Canceled: { label: "Annulé", className: "bg-muted text-muted-foreground" },
  WaitingBankValidation: { label: "Validation bancaire", className: "bg-warning text-warning-foreground hover:bg-warning/90" },
  WaitingBankWithdraw: { label: "Prélèvement en attente", className: "bg-warning text-warning-foreground hover:bg-warning/90" },
  Unknown: { label: "Inconnu", className: "bg-muted text-muted-foreground" },
};

export function paymentStateBadge(state: string | null | undefined) {
  if (!state) return <Badge variant="outline">—</Badge>;
  const cfg = PAYMENT_STATE_MAP[state];
  if (!cfg) return <Badge variant="outline">{state}</Badge>;
  return <Badge className={cfg.className}>{cfg.label}</Badge>;
}

function OrderDetailPage() {
  const { id } = Route.useParams();
  const { hasAccess } = useAuth();
  const canImport = hasAccess("treasury", "write");
  const [importOpen, setImportOpen] = useState(false);

  const { data, isLoading, error } = useQuery({
    queryKey: ["helloasso-order", id],
    queryFn: () => fetchHelloAssoOrder(id),
  });

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Button asChild variant="ghost" size="sm">
          <Link to="/orders">
            <ArrowLeft className="mr-2 h-4 w-4" /> Retour aux commandes
          </Link>
        </Button>
        <Card className="p-8 text-center text-muted-foreground">Chargement…</Card>
      </div>
    );
  }
  if (error || !data) {
    return (
      <div className="space-y-6">
        <Button asChild variant="ghost" size="sm">
          <Link to="/orders">
            <ArrowLeft className="mr-2 h-4 w-4" /> Retour aux commandes
          </Link>
        </Button>
        <Card className="p-8 text-center text-destructive">
          Commande introuvable.
        </Card>
      </div>
    );
  }

  const o = data;
  const fullName = [o.payer_first_name, o.payer_last_name].filter(Boolean).join(" ") || "—";

  return (
    <div className="space-y-6">
      <Button asChild variant="ghost" size="sm">
        <Link to="/orders">
          <ArrowLeft className="mr-2 h-4 w-4" /> Retour aux commandes
        </Link>
      </Button>

      <PageHeader
        title={`Commande #${o.external_id}`}
        description={`${o.form_name ?? "—"} · ${fmtDateTime(o.order_date)}`}
        actions={
          canImport ? (
            <Button onClick={() => setImportOpen(true)}>
              <Download className="mr-2 h-4 w-4" />
              Importer les items
            </Button>
          ) : null
        }
      />

      <div className="grid gap-4 md:grid-cols-3">
        <Card className="p-4">
          <div className="text-xs uppercase tracking-wide text-muted-foreground">Total</div>
          <div className="mt-1 text-2xl font-semibold">{fmtEUR(o.amount_total)}</div>
          <div className="mt-1 text-xs text-muted-foreground">
            TVA {fmtEUR(o.amount_vat)} · Remise {fmtEUR(o.amount_discount)}
          </div>
        </Card>
        <Card className="p-4">
          <div className="text-xs uppercase tracking-wide text-muted-foreground">Items</div>
          <div className="mt-1 text-2xl font-semibold">{o.items_count}</div>
          <div className="mt-1 text-xs text-muted-foreground">{o.payments_count} paiement(s)</div>
        </Card>
        <Card className="p-4">
          <div className="text-xs uppercase tracking-wide text-muted-foreground">Versement</div>
          <div className="mt-1 text-lg font-semibold">{o.cash_out_state ?? "—"}</div>
          <div className="mt-1 text-xs text-muted-foreground">
            {o.cash_out_date ? fmtDate(o.cash_out_date) : "Non versé"}
          </div>
        </Card>
      </div>

      <Card className="p-5">
        <h3 className="font-display text-lg font-semibold mb-3">Payeur</h3>
        <dl className="grid gap-3 text-sm sm:grid-cols-2">
          <div>
            <dt className="text-muted-foreground text-xs uppercase">Nom</dt>
            <dd>{fullName}</dd>
          </div>
          <div>
            <dt className="text-muted-foreground text-xs uppercase">Email</dt>
            <dd>{o.payer_email ?? "—"}</dd>
          </div>
          <div>
            <dt className="text-muted-foreground text-xs uppercase">Société</dt>
            <dd>{o.payer_company ?? "—"}</dd>
          </div>
          <div>
            <dt className="text-muted-foreground text-xs uppercase">Date de naissance</dt>
            <dd>{fmtDate(o.payer_date_of_birth)}</dd>
          </div>
          <div className="sm:col-span-2">
            <dt className="text-muted-foreground text-xs uppercase">Adresse</dt>
            <dd>
              {[o.payer_address, o.payer_postal_code, o.payer_city, o.payer_country]
                .filter(Boolean).join(", ") || "—"}
            </dd>
          </div>
        </dl>
      </Card>

      <Card>
        <div className="flex items-center justify-between p-4 border-b">
          <h3 className="font-display text-lg font-semibold">Items du panier</h3>
          <Badge variant="outline">{o.items.length} ligne{o.items.length > 1 ? "s" : ""}</Badge>
        </div>
        {o.items.length === 0 ? (
          <div className="p-6 text-center text-muted-foreground text-sm">Aucun item.</div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Nom</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Bénéficiaire</TableHead>
                <TableHead className="text-center">Qté</TableHead>
                <TableHead className="text-right">Montant</TableHead>
                <TableHead>État</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {o.items.map((it) => {
                const benef = [it.user_first_name, it.user_last_name].filter(Boolean).join(" ");
                const hasFields = Array.isArray(it.custom_fields) && it.custom_fields.length > 0;
                const hasOptions = Array.isArray(it.options) && it.options.length > 0;
                return (
                  <Fragment key={it.id}>
                    <TableRow>
                      <TableCell>
                        <div className="font-medium">{it.name ?? "—"}</div>
                        {it.tier_description && (
                          <div className="text-xs text-muted-foreground">{it.tier_description}</div>
                        )}
                        {it.discount_code && (
                          <div className="text-xs text-primary">
                            Code {it.discount_code} (−{fmtEUR(it.discount_amount)})
                          </div>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge variant="secondary">{it.type ?? "—"}</Badge>
                      </TableCell>
                      <TableCell>{benef || "—"}</TableCell>
                      <TableCell className="text-center">{it.quantity}</TableCell>
                      <TableCell className="text-right tabular-nums">{fmtEUR(it.amount)}</TableCell>
                      <TableCell>
                        {itemStateBadge(it.state)}
                      </TableCell>
                    </TableRow>
                    {(hasFields || hasOptions || it.comment) && (
                      <TableRow className="bg-muted/20">
                        <TableCell colSpan={6} className="text-xs">
                          {it.comment && (
                            <div className="mb-2">
                              <span className="font-semibold">Commentaire : </span>
                              {it.comment}
                            </div>
                          )}
                          {hasFields && (
                            <div className="mb-2">
                              <div className="font-semibold mb-1">Champs personnalisés</div>
                              <ul className="space-y-0.5">
                                {it.custom_fields!.map((f, i) => (
                                  <li key={i}>
                                    <span className="text-muted-foreground">{f.name} :</span>{" "}
                                    {f.answer || "—"}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                          {hasOptions && (
                            <div>
                              <div className="font-semibold mb-1">Options</div>
                              <ul className="space-y-0.5">
                                {it.options!.map((opt, i) => (
                                  <li key={i}>
                                    {opt.name}
                                    {typeof opt.amount === "number" && opt.amount > 0 && (
                                      <span className="text-muted-foreground"> · {fmtEUR(opt.amount / 100)}</span>
                                    )}
                                    {opt.isRequired && (
                                      <span className="ml-1 text-muted-foreground">(requis)</span>
                                    )}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </TableCell>
                      </TableRow>
                    )}
                  </Fragment>
                );
              })}
            </TableBody>
          </Table>
        )}
      </Card>

      <Card>
        <div className="flex items-center justify-between p-4 border-b">
          <h3 className="font-display text-lg font-semibold">Paiements</h3>
          <Badge variant="outline">{o.payments.length} échéance{o.payments.length > 1 ? "s" : ""}</Badge>
        </div>
        {o.payments.length === 0 ? (
          <div className="p-6 text-center text-muted-foreground text-sm">Aucun paiement.</div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead>
                <TableHead>ID HelloAsso</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Moyen</TableHead>
                <TableHead className="text-right">Montant</TableHead>
                <TableHead className="text-right">Pourboire</TableHead>
                <TableHead>État</TableHead>
                <TableHead>Versement</TableHead>
                <TableHead className="w-12" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {o.payments.map((p) => (
                <TableRow key={p.id}>
                  <TableCell className="whitespace-nowrap text-sm">
                    {fmtDateTime(p.payment_date)}
                    {p.installment_number && (
                      <div className="text-xs text-muted-foreground">
                        Échéance #{p.installment_number}
                      </div>
                    )}
                  </TableCell>
                  <TableCell className="font-mono text-xs">{p.payment_external_id ?? "—"}</TableCell>
                  <TableCell>{p.type ?? "—"}</TableCell>
                  <TableCell>{p.payment_means ?? "—"}</TableCell>
                  <TableCell className="text-right tabular-nums">{fmtEUR(p.amount)}</TableCell>
                  <TableCell className="text-right tabular-nums text-muted-foreground">
                    {p.amount_tip ? fmtEUR(p.amount_tip) : "—"}
                  </TableCell>
                  <TableCell>{paymentStateBadge(p.state)}</TableCell>
                  <TableCell>
                    <div className="text-sm">{p.cash_out_state ?? "—"}</div>
                    {p.cash_out_date && (
                      <div className="text-xs text-muted-foreground">{fmtDate(p.cash_out_date)}</div>
                    )}
                  </TableCell>
                  <TableCell>
                    {p.payment_receipt_url ? (
                      <a
                        href={p.payment_receipt_url}
                        target="_blank" rel="noreferrer"
                        title="Reçu de paiement"
                        className="inline-flex h-8 w-8 items-center justify-center rounded hover:bg-muted"
                      >
                        <FileText className="h-4 w-4" />
                      </a>
                    ) : null}
                    {p.fiscal_receipt_url ? (
                      <a
                        href={p.fiscal_receipt_url}
                        target="_blank" rel="noreferrer"
                        title="Reçu fiscal"
                        className="inline-flex h-8 w-8 items-center justify-center rounded hover:bg-muted"
                      >
                        <ExternalLink className="h-4 w-4" />
                      </a>
                    ) : null}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>

      <OrderImportDialog
        orderId={importOpen ? id : null}
        onClose={() => setImportOpen(false)}
      />
    </div>
  );
}
